/**
 * Vector Store Management Dashboard
 * Administrative interface for ChromaDB collections, embeddings, and vector health monitoring
 * Simple UI for users to understand and manage their vector knowledge base
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
import { 
  Database, Search, BarChart3, Settings, RefreshCw,
  Layers, Network, Zap, AlertTriangle, CheckCircle2,
  Eye, Trash2, Download, Filter, Clock
} from 'lucide-react';

interface VectorCollection {
  id: string;
  name: string;
  description: string;
  documentCount: number;
  embeddingCount: number;
  size: string;
  lastUpdated: Date;
  status: 'healthy' | 'syncing' | 'error';
  metadata: {
    model: string;
    dimensions: number;
    indexType: string;
    organizationId: string;
  };
}

interface VectorMetrics {
  totalCollections: number;
  totalDocuments: number;
  totalEmbeddings: number;
  storageUsed: number;
  avgSimilarityScore: number;
  queryPerformance: number;
  healthScore: number;
}

interface SearchQuery {
  id: string;
  query: string;
  timestamp: Date;
  resultsFound: number;
  responseTime: number;
  similarityScores: number[];
}

interface VectorStoreManagerProps {
  organizationId: string;
  onCollectionSelect?: (collection: VectorCollection) => void;
}

export const VectorStoreManager: React.FC<VectorStoreManagerProps> = ({
  organizationId,
  onCollectionSelect
}) => {
  const [collections, setCollections] = useState<VectorCollection[]>([]);
  const [metrics, setMetrics] = useState<VectorMetrics>({
    totalCollections: 0,
    totalDocuments: 0,
    totalEmbeddings: 0,
    storageUsed: 0,
    avgSimilarityScore: 0,
    queryPerformance: 0,
    healthScore: 0
  });
  const [recentQueries, setRecentQueries] = useState<SearchQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'collections' | 'search' | 'health'>('overview');

  useEffect(() => {
    loadVectorData();
    const interval = setInterval(loadVectorData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [organizationId]);

  const loadVectorData = async () => {
    try {
      // Simulate API call - replace with actual ChromaDB API integration
      const mockCollections: VectorCollection[] = [
        {
          id: 'org_main',
          name: `Organization ${organizationId} - Main`,
          description: 'Primary knowledge base collection for organization documents',
          documentCount: 127,
          embeddingCount: 15420,
          size: '2.3 GB',
          lastUpdated: new Date(),
          status: 'healthy',
          metadata: {
            model: 'text-embedding-ada-002',
            dimensions: 1536,
            indexType: 'cosine',
            organizationId
          }
        },
        {
          id: 'org_campaigns',
          name: `Organization ${organizationId} - Campaigns`,
          description: 'Campaign-specific documents and marketing materials',
          documentCount: 89,
          embeddingCount: 10240,
          size: '1.8 GB',
          lastUpdated: new Date(Date.now() - 3600000),
          status: 'healthy',
          metadata: {
            model: 'text-embedding-ada-002',
            dimensions: 1536,
            indexType: 'cosine',
            organizationId
          }
        },
        {
          id: 'org_products',
          name: `Organization ${organizationId} - Products`,
          description: 'Product documentation, specifications, and technical content',
          documentCount: 234,
          embeddingCount: 28650,
          size: '4.1 GB',
          lastUpdated: new Date(Date.now() - 1800000),
          status: 'syncing',
          metadata: {
            model: 'text-embedding-ada-002',
            dimensions: 1536,
            indexType: 'cosine',
            organizationId
          }
        }
      ];

      setCollections(mockCollections);

      // Calculate metrics
      const totalDocs = mockCollections.reduce((sum, col) => sum + col.documentCount, 0);
      const totalEmb = mockCollections.reduce((sum, col) => sum + col.embeddingCount, 0);
      const totalSize = mockCollections.reduce((sum, col) => sum + parseFloat(col.size), 0);

      setMetrics({
        totalCollections: mockCollections.length,
        totalDocuments: totalDocs,
        totalEmbeddings: totalEmb,
        storageUsed: totalSize,
        avgSimilarityScore: 0.847,
        queryPerformance: 89,
        healthScore: 94
      });

      // Mock recent queries
      setRecentQueries([
        {
          id: 'q1',
          query: 'product features and benefits',
          timestamp: new Date(Date.now() - 300000),
          resultsFound: 23,
          responseTime: 145,
          similarityScores: [0.92, 0.87, 0.84, 0.81, 0.79]
        },
        {
          id: 'q2',
          query: 'pricing strategy competitive analysis',
          timestamp: new Date(Date.now() - 600000),
          resultsFound: 18,
          responseTime: 98,
          similarityScores: [0.89, 0.85, 0.82, 0.78, 0.76]
        }
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Failed to load vector data:', error);
      setLoading(false);
    }
  };

  const handleTestQuery = async () => {
    if (!testQuery.trim()) return;

    try {
      // Simulate search - replace with actual API call
      const mockResults = [
        {
          content: "Our product offers advanced AI-powered analytics that help businesses make data-driven decisions...",
          similarity: 0.92,
          source: "Product_Overview_2024.pdf",
          chunk: 1
        },
        {
          content: "The competitive landscape shows that our pricing strategy positions us favorably against...",
          similarity: 0.87,
          source: "Market_Analysis.docx",
          chunk: 3
        },
        {
          content: "Customer feedback indicates strong satisfaction with our user interface and ease of use...",
          similarity: 0.84,
          source: "Customer_Survey_Q3.pdf",
          chunk: 2
        }
      ];

      setTestResults(mockResults);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Key Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Vector Database Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Overall Health</span>
                <span className="text-sm text-gray-600">{metrics.healthScore}%</span>
              </div>
              <Progress value={metrics.healthScore} className="h-2" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-blue-600">{metrics.totalCollections}</div>
                <div className="text-xs text-gray-600">Collections</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">{metrics.totalDocuments}</div>
                <div className="text-xs text-gray-600">Documents</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Storage & Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-green-600" />
            Storage & Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm">Total Embeddings</span>
              <span className="text-sm font-semibold">{metrics.totalEmbeddings.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Storage Used</span>
              <span className="text-sm font-semibold">{metrics.storageUsed.toFixed(1)} GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Avg Query Time</span>
              <span className="text-sm font-semibold">{metrics.queryPerformance}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Similarity Score</span>
              <span className="text-sm font-semibold">{metrics.avgSimilarityScore.toFixed(3)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-purple-600" />
            Recent Queries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentQueries.slice(0, 3).map(query => (
              <div key={query.id} className="border-l-4 border-blue-200 pl-3">
                <div className="text-sm font-medium truncate">{query.query}</div>
                <div className="text-xs text-gray-600 flex justify-between">
                  <span>{query.resultsFound} results</span>
                  <span>{query.responseTime}ms</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderCollections = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Vector Collections</h2>
        <Button onClick={loadVectorData} variant="secondary">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {collections.map(collection => (
          <Card key={collection.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{collection.name}</h3>
                    <Badge variant={collection.status === 'healthy' ? 'default' : 'secondary'}>
                      {collection.status}
                    </Badge>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{collection.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Documents:</span> {collection.documentCount}
                    </div>
                    <div>
                      <span className="font-medium">Embeddings:</span> {collection.embeddingCount.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Size:</span> {collection.size}
                    </div>
                    <div>
                      <span className="font-medium">Model:</span> {collection.metadata.model}
                    </div>
                  </div>
                  
                  <div className="mt-3 text-xs text-gray-500">
                    Last updated: {collection.lastUpdated.toLocaleDateString()} at {collection.lastUpdated.toLocaleTimeString()}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      setSelectedCollection(collection.id);
                      onCollectionSelect?.(collection);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderSearch = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Test Vector Search</h2>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter search query to test vector similarity..."
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleTestQuery()}
                />
              </div>
              <Button onClick={handleTestQuery}>
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {testResults.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Search Results</h3>
          <div className="space-y-3">
            {testResults.map((result, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="secondary">
                          {(result.similarity * 100).toFixed(1)}% match
                        </Badge>
                        <span className="text-sm text-gray-600">{result.source}</span>
                      </div>
                      <p className="text-gray-800">{result.content}</p>
                      <div className="mt-2 text-xs text-gray-500">
                        Chunk {result.chunk} • Similarity: {result.similarity.toFixed(3)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderHealth = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">System Health Monitor</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Operational Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="text-sm font-medium">ChromaDB Connection</span>
                  <Badge className="bg-green-100 text-green-800">Healthy</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="text-sm font-medium">OpenAI Embeddings</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="text-sm font-medium">Vector Indexing</span>
                  <Badge className="bg-green-100 text-green-800">Optimal</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                  <span className="text-sm font-medium">Auto-Processing</span>
                  <Badge className="bg-yellow-100 text-yellow-800">Syncing</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">Query Response Time</span>
                    <span className="text-sm text-gray-600">{metrics.queryPerformance}ms</span>
                  </div>
                  <Progress value={Math.min(100, (200 - metrics.queryPerformance) / 2)} className="h-2" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">Embedding Quality</span>
                    <span className="text-sm text-gray-600">{(metrics.avgSimilarityScore * 100).toFixed(1)}%</span>
                  </div>
                  <Progress value={metrics.avgSimilarityScore * 100} className="h-2" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">Storage Efficiency</span>
                    <span className="text-sm text-gray-600">87%</span>
                  </div>
                  <Progress value={87} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading vector store data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vector Store Manager</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Health Score: <span className="font-semibold text-green-600">{metrics.healthScore}%</span>
          </div>
          <Button variant="secondary" onClick={loadVectorData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'collections', label: 'Collections', icon: Database },
          { id: 'search', label: 'Test Search', icon: Search },
          { id: 'health', label: 'Health', icon: CheckCircle2 }
        ].map(tab => (
          <button
            key={tab.id}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab(tab.id as any)}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'collections' && renderCollections()}
      {activeTab === 'search' && renderSearch()}
      {activeTab === 'health' && renderHealth()}
    </div>
  );
};

export default VectorStoreManager;